import React from 'react';
import type {CompositeEvidence, EvidenceGroup, EvidencePattern, EvidenceZone} from '../../../../types/compositeSignal';

interface EvidenceChainBlockProps {
    evidence: CompositeEvidence | null;
}

function directionText(dir: number | undefined): string {
    if (1 === dir) return '看多';
    if (-1 === dir) return '看空';
    return '中性';
}

function PatternRow({p}: { p: EvidencePattern }) {
    return (
        <div className="ev-row">
            <span className="ev-name">{p.patternName}</span>
            <span className="ev-code">{p.patternCode}</span>
            <span className={`ev-dir ${1 === p.direction ? 'bull' : -1 === p.direction ? 'bear' : 'neutral'}`}>
                {directionText(p.direction)}
            </span>
            <span className="ev-conf">置信度 {p.confidence}</span>
        </div>
    );
}

function ZoneRow({z}: { z: EvidenceZone }) {
    return (
        <div className="ev-row">
            <span className="ev-name">{z.patternCode}</span>
            <span className="ev-code">{Number(z.zoneLower)} ~ {Number(z.zoneUpper)}</span>
            <span className="ev-conf">触及 {z.touchCount} 次 · 置信度 {z.confidence}</span>
        </div>
    );
}

/**
 * 冲突态单侧证据组（多/空并列展示）
 */
function GroupBlock({title, group, side}: { title: string; group: EvidenceGroup; side: 'bull' | 'bear' }) {
    return (
        <div className={`ev-group ${side}`}>
            <div className="ev-group-title">{title}</div>
            {group.patterns?.map((p, i) => <PatternRow key={`p${i}`} p={p}/>)}
            {group.zones?.map((z, i) => <ZoneRow key={`z${i}`} z={z}/>)}
        </div>
    );
}

/**
 * 证据链完整视图：主证据 + 落点区域 + 附加证据 + 结论；
 * 冲突态切换为多空双列对照布局
 */
export const EvidenceChainBlock: React.FC<EvidenceChainBlockProps> = ({evidence}) => {
    if (!evidence) {
        return <div className="evidence-chain empty">暂无证据数据</div>;
    }
    const isConflict = !!evidence.bullGroup && !!evidence.bearGroup;
    return (
        <div className="evidence-chain">
            {isConflict ? (
                <>
                    <div className="ev-columns">
                        <GroupBlock title="多方证据" group={evidence.bullGroup!} side="bull"/>
                        <GroupBlock title="空方证据" group={evidence.bearGroup!} side="bear"/>
                    </div>
                    <div className="ev-reason">观望态不提供入场/止损/目标</div>
                </>
            ) : (
                <>
                    {evidence.primary && (
                        <div className="ev-section">
                            <div className="ev-label">主证据</div>
                            <PatternRow p={evidence.primary}/>
                        </div>
                    )}
                    {evidence.zone && (
                        <div className="ev-section">
                            <div className="ev-label">落点区域</div>
                            <ZoneRow z={evidence.zone}/>
                        </div>
                    )}
                    {!!evidence.extraPatterns?.length && (
                        <div className="ev-section">
                            <div className="ev-label">附加同向证据</div>
                            {evidence.extraPatterns.map((p, i) => <PatternRow key={i} p={p}/>)}
                        </div>
                    )}
                </>
            )}
            {evidence.reasonText && <div className="ev-reason">{evidence.reasonText}</div>}
        </div>
    );
};
